/**
 * LoRA Wizard Backend Controller
 *
 * Real-mode counterpart to {@link useLoraWizardState}: instead of running a
 * simulated training loop in local state, this hook stitches together the
 * Phase 06 TanStack-Query hooks plus the existing `useAssets` upload path
 * so the wizard can drive a genuine LoRA training run end-to-end.
 *
 * Used by `LoraWizard.tsx` when a real `loraId` is supplied (placeholder
 * passports 1..4 still drive the simulator). Slides receive the normalised
 * shape via props and render real data when present.
 */

import { useCallback, useMemo, useState } from "react";

import {
  useLora,
  useLoraBaseModels,
  useTrainingRun,
  useTrainingRuns,
  useSubmitTraining,
  useCancelTraining,
  useUpdateLora,
} from "@/hooks/loras";
import { useAssets } from "@/hooks/useAssets";
import type { Asset, AssetWithDerived } from "@/types/asset.types";
import type {
  Lora,
  LoraBaseModel,
  TrainingRun,
  TrainingRunWithCheckpoints,
} from "@/types/loras";

export interface LoraWizardBackend {
  // Catalog
  baseModels: LoraBaseModel[];
  baseModelsLoading: boolean;
  selectedBaseModel: LoraBaseModel | undefined;

  // LoRA
  lora: Lora | undefined;
  loraLoading: boolean;
  triggerWord: string;

  // Dataset
  datasetProjectId: string | undefined;
  datasetAssets: AssetWithDerived[];
  datasetAssetsLoading: boolean;
  uploadDatasetFile: (file: File) => Promise<Asset | null>;
  removeDatasetAsset: (asset: Asset) => Promise<boolean>;
  isUploadingDatasetAsset: boolean;

  // Active training run
  activeTrainingRunId: string | undefined;
  setActiveTrainingRunId: (id: string | undefined) => void;
  trainingRun: TrainingRunWithCheckpoints | undefined;
  trainingRunLoading: boolean;

  // Mutations
  submitTraining: (
    params: Record<string, unknown>,
    /** When true, uses mock training (30s per checkpoint) instead of real AWS Batch. */
    mock?: boolean,
  ) => Promise<TrainingRun | undefined>;
  isSubmittingTraining: boolean;
  submitTrainingError: Error | null;

  cancelTraining: () => Promise<void>;
  isCancellingTraining: boolean;

  updateTriggerWord: (word: string) => Promise<void>;
  isUpdatingTriggerWord: boolean;
}

const TERMINAL_TRAINING_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
]);

/**
 * @param loraId - UUID of the LoRA being operated on. Must be a real backend
 *   LoRA; placeholder demo IDs (1..4) should not reach this hook.
 */
export function useLoraWizardBackend(loraId: string): LoraWizardBackend {
  const loraQuery = useLora(loraId);
  const baseModelsQuery = useLoraBaseModels();
  const trainingRunsQuery = useTrainingRuns(loraId);

  // Active training run resolution:
  // 1. If we just submitted in this session, use that ID (set on success).
  // 2. Otherwise, fall back to the most-recent non-terminal run from the API
  //    (so a refresh mid-training resumes correctly).
  const [activeTrainingRunIdState, setActiveTrainingRunIdState] = useState<
    string | undefined
  >(undefined);

  const fallbackActiveRunId = useMemo<string | undefined>(() => {
    const runs = trainingRunsQuery.data;
    if (!runs || runs.length === 0) return undefined;
    // Runs come back ordered by created_at desc per Phase 02 contract.
    const nonTerminal = runs.find(
      (r) => !TERMINAL_TRAINING_STATUSES.has(r.statusSlug),
    );
    return (nonTerminal ?? runs[0])?.id;
  }, [trainingRunsQuery.data]);

  const activeTrainingRunId = activeTrainingRunIdState ?? fallbackActiveRunId;
  const trainingRunQuery = useTrainingRun(activeTrainingRunId);

  const lora = loraQuery.data;
  const baseModels = baseModelsQuery.data ?? [];
  const selectedBaseModel = lora
    ? baseModels.find((m) => m.slug === lora.baseModelSlug)
    : undefined;

  const datasetProjectId = lora?.datasetProjectId;

  // `useAssets` returns `[]` while projectId is undefined; we just pass through.
  const datasetAssetsHook = useAssets(datasetProjectId);

  const submitMutation = useSubmitTraining();
  const cancelMutation = useCancelTraining();
  const updateMutation = useUpdateLora(loraId);

  const submitTraining = useCallback<LoraWizardBackend["submitTraining"]>(
    async (params, mock) => {
      const run = await submitMutation.mutateAsync({ loraId, params, mock });
      setActiveTrainingRunIdState(run.id);
      return run;
    },
    [submitMutation, loraId],
  );

  const cancelTraining = useCallback<
    LoraWizardBackend["cancelTraining"]
  >(async () => {
    const runId = activeTrainingRunId;
    if (!runId) return;
    await cancelMutation.mutateAsync(runId);
  }, [cancelMutation, activeTrainingRunId]);

  const updateTriggerWord = useCallback<LoraWizardBackend["updateTriggerWord"]>(
    async (word) => {
      const trimmed = word.trim();
      await updateMutation.mutateAsync({
        triggerWord: trimmed.length > 0 ? trimmed : null,
      });
    },
    [updateMutation],
  );

  return {
    baseModels,
    baseModelsLoading: baseModelsQuery.isLoading,
    selectedBaseModel,

    lora,
    loraLoading: loraQuery.isLoading,
    triggerWord: lora?.triggerWord ?? "",

    datasetProjectId,
    datasetAssets: datasetAssetsHook.assets,
    datasetAssetsLoading: datasetAssetsHook.isLoading,
    uploadDatasetFile: datasetAssetsHook.uploadAsset,
    removeDatasetAsset: datasetAssetsHook.removeAsset,
    isUploadingDatasetAsset: datasetAssetsHook.isUploading,

    activeTrainingRunId,
    setActiveTrainingRunId: setActiveTrainingRunIdState,
    trainingRun: trainingRunQuery.data,
    trainingRunLoading: trainingRunQuery.isLoading,

    submitTraining,
    isSubmittingTraining: submitMutation.isPending,
    submitTrainingError: submitMutation.error,

    cancelTraining,
    isCancellingTraining: cancelMutation.isPending,

    updateTriggerWord,
    isUpdatingTriggerWord: updateMutation.isPending,
  };
}
